import React, { useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import i18next from 'i18next'
import {
  CameraIcon as AddPhotosIcon,
  EllipsisVerticalIcon as OptionsIcon,
} from '@heroicons/react/24/outline'
import GalleryView from '../../components/GalleryView'
import DropZoneWrapper from '../../components/DropZoneWrapper/DropZoneWrapper.component'
import { WorkspacesStore } from '../../stores/WorkspaceStore'
import { WORKSPACES_STORE } from '../../stores'
import { inject } from 'mobx-react'
import { SectionHeader } from '../../components/SectionHeader/SectionHeader'
import { TwButton } from '../../components/Button/TwButton'
import {
  FiltersButton,
  FiltersDrawer,
  useGalleryFilters,
  dateFromToApiDate,
  dateToToApiDate,
} from '../../components/FiltersDrawer'
import { GallerySort, useGallerySort } from '../../components/GallerySort'
import { GalleryOptionsMenu } from '../../components/GalleryOptions/GalleryOptionsMenu.component'
import { Tooltip } from '../../components/Tooltip/Tooltip.component'
import {
  getGroupByDatePreference,
  setGroupByDatePreference,
  getLayoutPreference,
  setLayoutPreference,
} from '../../utils/galleryPreferences'
import { GalleryLayoutMode } from '../../components/JustifiedGallery'

interface GalleryScreenParams {
  tag: string
  albumId: string
  workspaceSlug: string
}
interface GalleryScreenProps extends RouteComponentProps<GalleryScreenParams> {
  [WORKSPACES_STORE]?: WorkspacesStore
}

const GalleryScreen: React.FC<GalleryScreenProps> = (props) => {
  const workspacesStore = props[WORKSPACES_STORE]
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [optionsOpen, setOptionsOpen] = useState(false)
  const [groupByDate, setGroupByDate] = useState(getGroupByDatePreference)
  const [layoutMode, setLayoutMode] = useState<GalleryLayoutMode>(getLayoutPreference)
  const { filters, activeCount, apply, clearAll } = useGalleryFilters(props.location, props.history)
  const hasAlbum = Boolean(filters.albumId)
  const { sort, apply: applySort } = useGallerySort(props.location, props.history, { hasAlbum })

  const onGroupByDateChange = (value: boolean) => {
    setGroupByDate(value)
    setGroupByDatePreference(value)
  }

  const onLayoutModeChange = (value: GalleryLayoutMode) => {
    setLayoutMode(value)
    setLayoutPreference(value)
  }

  const isSearching = Boolean(filters.query)

  const workspace = workspacesStore.getWorkspaceBySlugOrId(props.match.params.workspaceSlug)
  if (!workspace) {
    setTimeout(() => props.history.replace('/'))
    return <div></div>
  } else if (workspace.id !== workspacesStore.activeWorkspaceId) {
    workspacesStore.setActiveWorkspace(workspace)
  }

  return (
    <DropZoneWrapper
      workspaceId={workspace.id}
      workspaceSlug={workspace.slug}
      history={props.history}
    >
      <GalleryView
        publicPhotos={false}
        groupByDate={groupByDate}
        layoutMode={layoutMode}
        orderBy={sort.orderBy}
        order={sort.order}
        query={filters.query}
        albumId={filters.albumId}
        personIds={filters.personIds}
        ownerIds={filters.ownerIds}
        deviceId={filters.deviceId}
        type={filters.type}
        dateFrom={filters.dateFrom ? dateFromToApiDate(filters.dateFrom) : undefined}
        dateTo={filters.dateTo ? dateToToApiDate(filters.dateTo) : undefined}
        history={props.history}
        match={props.match}
        location={props.location}
        workspaceId={workspace.id}
        workspaceSlug={workspace.slug}
      >
        <SectionHeader
          title={isSearching ? filters.query : 'Twoje najnowsze zdjęcia'}
          description={
            isSearching
              ? `Wyniki wyszukiwania dla: "${filters.query}".`
              : 'Zbiór Twoich wszystkich zdjęć począwszy od nowo dodanych.'
          }
          actions={
            <>
              <TwButton
                variant="outline"
                icon={<AddPhotosIcon className="!h-4 !w-4" />}
                onClick={() => props.history.push(`/${workspace.slug}/upload`)}
                className="inline-flex"
              >
                Dodaj zdjęcia
              </TwButton>
              <div className="ml-auto flex items-center gap-2">
                {/* semantic search is always relevance-ranked — the server ignores ordering */}
                {!isSearching && (
                  <GallerySort
                    sort={sort}
                    onChange={applySort}
                    allowAddedToAlbum={hasAlbum}
                  />
                )}
                <FiltersButton
                  activeCount={activeCount}
                  onClick={() => setFiltersOpen(true)}
                  onClear={clearAll}
                />
                <div className="relative">
                  <Tooltip label={i18next.t('galleryOptions.title')} align="right">
                    <TwButton
                      variant="outline"
                      icon={<OptionsIcon />}
                      onClick={() => setOptionsOpen(true)}
                      aria-label={i18next.t('galleryOptions.title')}
                    />
                  </Tooltip>
                  <GalleryOptionsMenu
                    isOpen={optionsOpen}
                    onClose={() => setOptionsOpen(false)}
                    groupByDate={groupByDate}
                    onGroupByDateChange={onGroupByDateChange}
                    layoutMode={layoutMode}
                    onLayoutModeChange={onLayoutModeChange}
                  />
                </div>
              </div>
            </>
          }
        />
      </GalleryView>
      <FiltersDrawer
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        workspaceId={workspace.id}
        filters={filters}
        onApply={apply}
      />
    </DropZoneWrapper>
  )
}

export default inject(WORKSPACES_STORE)(GalleryScreen)
